// Staff billing console: who owes what, one doctor group's statement, the payment-verification
// queue, and the company accounts clinics pay into.
//
// The statement here is byte-for-byte the one the doctor reads in the portal — same endpoint shape,
// same running balance — so a phone call about "my statement says…" never turns into two versions
// of the truth. What staff get on top is the ability to POST to it.
import { useCallback, useEffect, useState } from 'react';
import {
  alignerTrayPrice,
  alignerPricingHint,
  formatDate,
  formatMoney,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  rupeesToPaisa,
  WF_LEDGER_ENTRY_LABELS,
  WF_LEDGER_ENTRY_TYPES,
  WF_PAYMENT_STATUS_LABELS,
  type PaymentMethod,
  type WfBankAccount,
  type WfBillingSummary,
  type WfLedgerEntry,
  type WfLedgerEntryType,
  type WfPayment,
  type WfPaymentStatus,
  type WfStatement,
} from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { PageHeader } from '../../ui/primitives.tsx';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useAlignerPricing } from '../../lib/useAlignerPricing.ts';
import { paymentProofUrl, useWfConfig, wfApi } from './workflowApi.ts';
import { Btn, ErrorText, Field, Modal, Tabs, fmtDT, formGridCls, inputCls } from './wfUi.tsx';
import { EditEntryModal } from '../clinics/ClinicLedger.tsx';

type Tab = 'balances' | 'payments' | 'accounts';

export function WfBillingPage() {
  const can = useCan();
  const [tab, setTab] = useState<Tab>('balances');
  const [pendingCount, setPendingCount] = useState(0);
  const loadCount = useCallback(() => {
    wfApi
      .billingPayments({ status: 'pending' })
      .then((p) => setPendingCount(p.length))
      .catch(() => setPendingCount(0));
  }, []);
  useEffect(loadCount, [loadCount]);
  useLiveEvent('workflow.billing', loadCount);

  return (
    <div className="space-y-4">
      <PageHeader title="Billing" subtitle="Doctor ledgers, statements and payment verification - money is integer PKR throughout" />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'balances', label: 'Balances' },
          { key: 'payments', label: 'Payments', count: pendingCount || undefined },
          { key: 'accounts', label: 'Bank accounts' },
        ]}
      />
      {tab === 'balances' && <Balances canPost={can('invoice:manage')} />}
      {tab === 'payments' && <Payments canDecide={can('finance:verify_payment')} canRecord={can('invoice:manage')} onChanged={loadCount} />}
      {tab === 'accounts' && <Accounts canEdit={can('invoice:manage')} />}
    </div>
  );
}

const money = (paisa: number) => formatMoney(paisa);

// ---------------------------------------------------------------------------------------------
// Balances + statement
// ---------------------------------------------------------------------------------------------

function Balances({ canPost }: { canPost: boolean }) {
  const [sum, setSum] = useState<WfBillingSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [charging, setCharging] = useState(false);
  const load = useCallback(() => {
    wfApi.billingSummary().then(setSum).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.billing', load);

  return (
    <div className="space-y-3">
      <ErrorText>{err}</ErrorText>
      <div className="flex flex-wrap items-center gap-3">
        {sum && (
          <>
            <Stat label="Total charged" value={money(sum.totals.charged)} />
            <Stat label="Received" value={money(sum.totals.paid)} />
            <Stat label="Outstanding" value={money(sum.totals.balance)} tone={sum.totals.balance > 0 ? 'bad' : 'good'} />
            {sum.totals.pendingCount > 0 && <Stat label={`Awaiting verification (${sum.totals.pendingCount})`} value={money(sum.totals.pendingAmount)} tone="warn" />}
          </>
        )}
        {canPost && (
          <Btn className="ml-auto" onClick={() => setCharging(true)}>
            ＋ Post a charge
          </Btn>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-level-1">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-[11px] uppercase tracking-wider text-muted">
            <tr>
              <th className="px-3 py-2">Group ID</th>
              <th className="px-3 py-2">Doctor / clinic</th>
              <th className="px-3 py-2 text-right">Charged</th>
              <th className="px-3 py-2 text-right">Received</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-right">Pending</th>
              <th className="px-3 py-2">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {sum?.rows.map((r) => (
              <tr key={r.doctorId} className="cursor-pointer border-t border-line hover:bg-surface-2" onClick={() => setOpen(r.doctorId)}>
                <td className="px-3 py-2 font-mono text-xs font-semibold">{r.code}</td>
                <td className="px-3 py-2">
                  {r.name}
                  {r.clinicName && <span className="text-muted"> · {r.clinicName}</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono">{money(r.charged)}</td>
                <td className="px-3 py-2 text-right font-mono">{money(r.paid)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${r.balance > 0 ? 'text-status-urgent-fg' : ''}`}>{money(r.balance)}</td>
                <td className="px-3 py-2 text-right font-mono text-muted">{r.pendingAmount ? money(r.pendingAmount) : ''}</td>
                <td className="px-3 py-2 text-xs text-muted">{r.lastEntryAt ? formatDate(r.lastEntryAt) : '-'}</td>
              </tr>
            ))}
            {sum?.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-muted">
                  No money has moved yet. Post a charge to start a doctor's ledger.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <StatementModal doctorId={open} canPost={canPost} onClose={() => setOpen(null)} onChanged={load} />}
      {charging && (
        <ChargeModal
          onClose={() => setCharging(false)}
          onDone={() => {
            setCharging(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'neutral' | 'bad' | 'good' | 'warn' }) {
  const tones = { neutral: 'border-line', bad: 'border-status-urgent-fg/40 bg-status-urgent-bg/40', good: 'border-status-progress-fg/30 bg-status-progress-bg/40', warn: 'border-amber-400/50 bg-amber-50' }[tone];
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones}`}>
      <div className="font-mono text-lg font-semibold text-ink">{value}</div>
      <div className="text-[11px] text-muted">{label}</div>
    </div>
  );
}

function StatementModal({ doctorId, canPost, onClose, onChanged }: { doctorId: string; canPost: boolean; onClose: () => void; onChanged: () => void }) {
  const [range, setRange] = useState({ from: '', to: '' });
  const [s, setS] = useState<WfStatement | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<string | null>(null);
  const [editing, setEditing] = useState<WfLedgerEntry | null>(null);
  const [reason, setReason] = useState('');
  const load = useCallback(() => {
    wfApi
      .statement(doctorId, { from: range.from || undefined, to: range.to || undefined })
      .then(setS)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [doctorId, range.from, range.to]);
  useEffect(load, [load]);

  const doVoid = async () => {
    if (!voiding || reason.trim().length < 3) return;
    try {
      await wfApi.voidEntry(voiding, reason.trim());
      setVoiding(null);
      setReason('');
      load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Modal open onClose={onClose} wide title={s ? `${s.doctor.code} - ${s.doctor.name}` : 'Statement'}>
      <div className="space-y-3">
        <ErrorText>{err}</ErrorText>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-muted">
            From
            <input type="date" className={inputCls} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </label>
          <label className="text-xs text-muted">
            To
            <input type="date" className={inputCls} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </label>
          {s && (
            <div className="ml-auto text-right">
              <div className={`font-mono text-2xl font-bold ${s.totals.closingBalance > 0 ? 'text-status-urgent-fg' : 'text-ink'}`}>{money(s.totals.closingBalance)}</div>
              <div className="text-[11px] text-muted">
                {money(s.totals.charges)} charged · {money(s.totals.payments + s.totals.adjustments)} settled
                {s.pendingAmount > 0 ? ` · ${money(s.pendingAmount)} pending` : ''}
              </div>
            </div>
          )}
        </div>

        <div className="max-h-[55vh] overflow-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface-2 text-left text-[11px] uppercase tracking-wider text-muted">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Details</th>
                <th className="px-3 py-2 text-right">Charge</th>
                <th className="px-3 py-2 text-right">Settled</th>
                <th className="px-3 py-2 text-right">Balance</th>
                {canPost && <th />}
              </tr>
            </thead>
            <tbody>
              {s && s.openingBalance !== 0 && (
                <tr className="bg-surface-2/50 text-muted">
                  <td className="px-3 py-2">{formatDate(s.from ?? '')}</td>
                  <td className="px-3 py-2 italic">Balance brought forward</td>
                  <td />
                  <td />
                  <td className="px-3 py-2 text-right font-mono">{money(s.openingBalance)}</td>
                  {canPost && <td />}
                </tr>
              )}
              {s?.entries.map((e) => (
                <tr key={e.id} className={`border-t border-line ${e.isVoid ? 'text-muted line-through' : ''}`}>
                  <td className="whitespace-nowrap px-3 py-2">{formatDate(e.entryDate)}</td>
                  <td className="px-3 py-2">
                    <div className="text-ink">{e.description}</div>
                    <div className="text-[11px] text-muted">
                      {WF_LEDGER_ENTRY_LABELS[e.entryType]}
                      {e.caseNumber ? ` · ${e.caseNumber}` : ''}
                      {e.createdByName ? ` · ${e.createdByName}` : ''}
                      {e.isVoid ? ` · Voided: ${e.voidReason ?? ''}` : ''}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{e.debit ? money(e.debit) : ''}</td>
                  <td className="px-3 py-2 text-right font-mono">{e.credit ? money(e.credit) : ''}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold">{money(e.balance)}</td>
                  {canPost && (
                    <td className="px-2 py-2 text-right">
                      {!e.isVoid && (
                        <span className="inline-flex gap-2">
                          <button className="text-xs text-accent hover:underline" onClick={() => setEditing(e)}>
                            Edit
                          </button>
                          <button className="text-xs text-status-urgent-fg hover:underline" onClick={() => setVoiding(e.id)}>
                            Void
                          </button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {s?.entries.length === 0 && (
                <tr>
                  <td colSpan={canPost ? 6 : 5} className="px-3 py-10 text-center text-muted">
                    Nothing on this account for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {editing && (
          <EditEntryModal
            entry={editing}
            onClose={() => setEditing(null)}
            onDone={() => {
              setEditing(null);
              load();
              onChanged();
            }}
          />
        )}
        {voiding && (
          <div className="rounded border border-status-urgent-fg/30 bg-status-urgent-bg/40 p-3">
            {/* A money entry is never edited or deleted - it is voided, and the reason is the record. */}
            <p className="mb-2 text-sm text-ink">Voiding removes this entry from the balance. The line stays on the statement with your reason.</p>
            <div className="flex flex-wrap gap-2">
              <input className={`${inputCls} flex-1`} autoFocus placeholder="Why is this being voided?" value={reason} onChange={(e) => setReason(e.target.value)} />
              <Btn tone="danger" disabled={reason.trim().length < 3} onClick={() => void doVoid()}>
                Void entry
              </Btn>
              <Btn
                tone="ghost"
                onClick={() => {
                  setVoiding(null);
                  setReason('');
                }}
              >
                Cancel
              </Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Post a charge
// ---------------------------------------------------------------------------------------------

function ChargeModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { doctors } = useWfConfig();
  const [f, setF] = useState({ doctorId: '', caseId: '', entryType: 'charge' as WfLedgerEntryType, amount: '', description: '', entryDate: new Date().toISOString().slice(0, 10), reference: '' });
  const [cases, setCases] = useState<Array<{ id: string; caseNumber: string; patientName: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [trays, setTrays] = useState(''); // quick clear-aligner pricing
  const pricing = useAlignerPricing();
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const paisa = rupeesToPaisa(f.amount);

  const trayCount = Math.max(0, Math.trunc(Number(trays) || 0));
  const alignerPrice = trayCount > 0 ? alignerTrayPrice(trayCount, pricing) : null;
  const applyAlignerPrice = () => {
    if (!alignerPrice) return;
    setF((p) => ({
      ...p,
      amount: String(alignerPrice.totalRupees),
      description:
        p.description.trim() ||
        `Clear aligners - ${alignerPrice.trays} trays @ PKR ${alignerPrice.perTrayRupees}/tray`,
    }));
  };

  // Offer this group's cases so a charge can be pinned to the work it is for.
  useEffect(() => {
    if (!f.doctorId) {
      setCases([]);
      return;
    }
    wfApi
      .doctorCases(f.doctorId)
      .then((r) => setCases(r.items.map((c) => ({ id: c.id, caseNumber: c.caseNumber, patientName: c.patientName }))))
      .catch(() => setCases([]));
  }, [f.doctorId]);

  const save = async () => {
    if (!f.doctorId || !paisa || paisa <= 0 || f.description.trim().length < 2) {
      setErr('Pick a doctor group, an amount and a description.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await wfApi.postCharge({
        doctorId: f.doctorId,
        caseId: f.caseId || null,
        entryType: f.entryType,
        amount: paisa,
        description: f.description.trim(),
        entryDate: f.entryDate || null,
        reference: f.reference || null,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Post to a doctor's ledger"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn disabled={busy} onClick={() => void save()}>
            {busy ? 'Posting…' : 'Post entry'}
          </Btn>
        </>
      }
    >
      <div className={formGridCls}>
        <ErrorText>{err}</ErrorText>
        <Field label="Doctor group *">
          <select className={inputCls} value={f.doctorId} onChange={(e) => setF((p) => ({ ...p, doctorId: e.target.value, caseId: '' }))}>
            <option value="">Select…</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} - {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Type *" hint="A charge adds to what they owe; everything else settles it.">
          <select className={inputCls} value={f.entryType} onChange={(e) => set('entryType', e.target.value)}>
            {WF_LEDGER_ENTRY_TYPES.filter((t) => t !== 'payment').map((t) => (
              <option key={t} value={t}>
                {WF_LEDGER_ENTRY_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Case" hint="Optional - links the entry to a case number on the statement.">
          <select className={inputCls} value={f.caseId} onChange={(e) => set('caseId', e.target.value)} disabled={!f.doctorId}>
            <option value="">Not case-specific</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.caseNumber} - {c.patientName}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Clear-aligner quick price"
          hint={alignerPricingHint(pricing)}
        >
          <div className="flex flex-wrap items-center gap-2">
            <input
              className={inputCls}
              style={{ maxWidth: '7rem' }}
              inputMode="numeric"
              placeholder="Trays"
              value={trays}
              onChange={(e) => setTrays(e.target.value)}
            />
            {alignerPrice && (
              <span className="text-sm text-muted">
                {alignerPrice.trays} × PKR {alignerPrice.perTrayRupees} ={' '}
                <b className="text-ink">{money(alignerPrice.totalPaisa)}</b>
              </span>
            )}
            <Btn tone="ghost" onClick={applyAlignerPrice} disabled={!alignerPrice}>
              Use this amount
            </Btn>
          </div>
        </Field>
        <Field label="Amount (PKR) *" hint={paisa && paisa > 0 ? `Posting ${money(paisa)}` : undefined}>
          <input className={inputCls} inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label="Description *">
          <input className={inputCls} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="e.g. Clear aligners - 20 stages, both arches" />
        </Field>
        <Field label="Date">
          <input type="date" className={inputCls} value={f.entryDate} onChange={(e) => set('entryDate', e.target.value)} />
        </Field>
        <Field label="Reference">
          <input className={inputCls} value={f.reference} onChange={(e) => set('reference', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Payment verification queue
// ---------------------------------------------------------------------------------------------

const statusTone: Record<WfPaymentStatus, string> = {
  pending: 'bg-status-pending-bg text-status-pending-fg',
  verified: 'bg-status-progress-bg text-status-progress-fg',
  rejected: 'bg-status-urgent-bg text-status-urgent-fg',
};

function Payments({ canDecide, canRecord, onChanged }: { canDecide: boolean; canRecord: boolean; onChanged: () => void }) {
  const [status, setStatus] = useState<'pending' | 'verified' | 'rejected' | 'all'>('pending');
  const [unverifying, setUnverifying] = useState<string | null>(null);
  const [items, setItems] = useState<WfPayment[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const load = useCallback(() => {
    wfApi
      .billingPayments({ status })
      .then(setItems)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [status]);
  useEffect(load, [load]);
  useLiveEvent('workflow.billing', load);

  const verify = async (id: string) => {
    setBusy(id);
    setErr(null);
    try {
      await wfApi.verifyPayment(id);
      load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  /**
   * Undo a verification: wrong clinic, or the transfer bounced.
   *
   * Not the same as rejecting a claim nobody accepted — this one already credited the doctor's
   * account and posted to the ledger, so both are reversed rather than erased.
   */
  const unverify = async () => {
    if (!unverifying || reason.trim().length < 3) return;
    setBusy(unverifying);
    try {
      await wfApi.unverifyPayment(unverifying, reason.trim());
      setUnverifying(null);
      setReason('');
      load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };
  const reject = async () => {
    if (!rejecting || reason.trim().length < 3) return;
    setBusy(rejecting);
    try {
      await wfApi.rejectPayment(rejecting, reason.trim());
      setRejecting(null);
      setReason('');
      load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-3">
      <ErrorText>{err}</ErrorText>
      <div className="flex flex-wrap items-center gap-2">
        {(['pending', 'verified', 'rejected', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${status === s ? 'bg-primary text-primary-fg' : 'bg-surface-2 text-muted hover:text-ink'}`}
          >
            {s}
          </button>
        ))}
        {canRecord && (
          <Btn className="ml-auto" tone="ghost" onClick={() => setRecording(true)}>
            ＋ Record a payment
          </Btn>
        )}
      </div>

      <div className="overflow-x-auto rounded-lg border border-line bg-surface shadow-level-1">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-[11px] uppercase tracking-wider text-muted">
            <tr>
              <th className="px-3 py-2">Payment</th>
              <th className="px-3 py-2">Doctor group</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2">Method</th>
              <th className="px-3 py-2">Paid on</th>
              <th className="px-3 py-2">Proof</th>
              <th className="px-3 py-2">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-line align-top">
                <td className="px-3 py-2">
                  <div className="font-mono text-xs font-semibold">{p.paymentNumber}</div>
                  <div className="text-[11px] text-muted">
                    {p.submittedSide === 'lab' ? 'Recorded by lab' : 'Submitted by clinic'} · {fmtDT(p.submittedAt)}
                  </div>
                  {p.note && <div className="mt-0.5 max-w-xs text-[11px] text-muted">{p.note}</div>}
                </td>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs font-semibold">{p.doctorCode}</span>
                  <div className="text-[11px] text-muted">{p.doctorName}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{money(p.amount)}</td>
                <td className="px-3 py-2">
                  {PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                  {p.bankAccountLabel && <div className="text-[11px] text-muted">into {p.bankAccountLabel}</div>}
                  {p.reference && <div className="font-mono text-[11px] text-muted">{p.reference}</div>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDate(p.paidOn)}</td>
                <td className="px-3 py-2">
                  {p.hasProof ? (
                    <a href={paymentProofUrl(p.id)} target="_blank" rel="noreferrer" className="text-xs font-medium text-accent hover:underline">
                      {p.proofName ?? 'View'}
                    </a>
                  ) : (
                    <span className="text-xs text-muted">None</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${statusTone[p.status]}`}>{WF_PAYMENT_STATUS_LABELS[p.status]}</span>
                  {p.decidedByName && <div className="text-[11px] text-muted">{p.decidedByName}</div>}
                  {p.decisionNote && <div className="max-w-[14rem] text-[11px] text-muted">{p.decisionNote}</div>}
                </td>
                <td className="px-3 py-2 text-right">
                  {p.status === 'pending' && canDecide && (
                    <div className="flex justify-end gap-1">
                      <Btn tone="accent" disabled={busy === p.id} onClick={() => void verify(p.id)}>
                        Verify
                      </Btn>
                      <Btn tone="ghost" disabled={busy === p.id} onClick={() => setRejecting(p.id)}>
                        Reject
                      </Btn>
                    </div>
                  )}
                  {p.status === 'verified' && canDecide && (
                    <Btn tone="ghost" disabled={busy === p.id} onClick={() => setUnverifying(p.id)}>
                      Undo verification
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-muted">
                  Nothing here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {unverifying && (
        <div className="rounded border border-status-warn-fg/30 bg-status-warn-bg/50 p-3">
          <p className="mb-2 text-sm text-status-warn-fg">
            This payment was already verified, so the credit on the doctor&rsquo;s account and the
            journal in the books are both <b>reversed</b> - nothing is deleted, and the record shows
            that the money arrived and then didn&rsquo;t.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              className={`${inputCls} flex-1`}
              autoFocus
              placeholder="Why is this being undone? (bounced, wrong clinic…)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Btn tone="danger" disabled={reason.trim().length < 3} onClick={() => void unverify()}>
              Undo verification
            </Btn>
            <Btn tone="ghost" onClick={() => { setUnverifying(null); setReason(''); }}>
              Cancel
            </Btn>
          </div>
        </div>
      )}

      {rejecting && (
        <div className="rounded border border-status-urgent-fg/30 bg-status-urgent-bg/40 p-3">
          <p className="mb-2 text-sm text-ink">The clinic sees this reason on their payment list. Nothing is posted to the ledger.</p>
          <div className="flex flex-wrap gap-2">
            <input className={`${inputCls} flex-1`} autoFocus placeholder="Why is this being rejected?" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Btn tone="danger" disabled={reason.trim().length < 3} onClick={() => void reject()}>
              Reject payment
            </Btn>
            <Btn
              tone="ghost"
              onClick={() => {
                setRejecting(null);
                setReason('');
              }}
            >
              Cancel
            </Btn>
          </div>
        </div>
      )}

      {recording && (
        <RecordPaymentModal
          onClose={() => setRecording(false)}
          onDone={() => {
            setRecording(false);
            load();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/** Money taken at the counter. It still lands as a pending claim so one person never both takes and clears it. */
function RecordPaymentModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { doctors } = useWfConfig();
  const [accounts, setAccounts] = useState<WfBankAccount[]>([]);
  const [f, setF] = useState({ doctorId: '', amount: '', method: 'cash' as PaymentMethod, bankAccountId: '', reference: '', paidOn: new Date().toISOString().slice(0, 10), note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const paisa = rupeesToPaisa(f.amount);
  useEffect(() => {
    wfApi.bankAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const save = async () => {
    if (!f.doctorId || !paisa || paisa <= 0) {
      setErr('Pick a doctor group and an amount.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await wfApi.recordPayment({
        doctorId: f.doctorId,
        amount: paisa,
        method: f.method,
        bankAccountId: f.bankAccountId || null,
        reference: f.reference || null,
        paidOn: f.paidOn,
        note: f.note || null,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a payment received"
      footer={
        <>
          <Btn tone="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Record payment'}
          </Btn>
        </>
      }
    >
      <div className={formGridCls}>
        <ErrorText>{err}</ErrorText>
        <p className="text-sm text-muted">This is filed as a pending payment - verify it from the queue to credit the ledger.</p>
        <Field label="Doctor group *">
          <select className={inputCls} value={f.doctorId} onChange={(e) => set('doctorId', e.target.value)}>
            <option value="">Select…</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} - {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Amount (PKR) *" hint={paisa && paisa > 0 ? `Recording ${money(paisa)}` : undefined}>
          <input className={inputCls} inputMode="decimal" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </Field>
        <Field label="Method *">
          <select className={inputCls} value={f.method} onChange={(e) => set('method', e.target.value)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
        {accounts.length > 0 && (
          <Field label="Into account">
            <select className={inputCls} value={f.bankAccountId} onChange={(e) => set('bankAccountId', e.target.value)}>
              <option value="">Not applicable</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} - {a.bankName}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Date paid *">
          <input type="date" className={inputCls} value={f.paidOn} onChange={(e) => set('paidOn', e.target.value)} />
        </Field>
        <Field label="Reference">
          <input className={inputCls} value={f.reference} onChange={(e) => set('reference', e.target.value)} />
        </Field>
        <Field label="Note">
          <textarea className={inputCls} rows={2} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Company bank accounts
// ---------------------------------------------------------------------------------------------

const EMPTY_ACCOUNT = { label: '', bankName: '', accountTitle: '', accountNumber: '', iban: '', branch: '', instructions: '', sortOrder: 0, isActive: true };

function Accounts({ canEdit }: { canEdit: boolean }) {
  const [items, setItems] = useState<WfBankAccount[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<(typeof EMPTY_ACCOUNT & { id?: string }) | null>(null);
  const load = useCallback(() => {
    wfApi.bankAccounts().then(setItems).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!editing) return;
    const { id, ...body } = editing;
    try {
      if (id) await wfApi.patchBankAccount(id, body);
      else await wfApi.createBankAccount(body);
      setEditing(null);
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-3">
      <ErrorText>{err}</ErrorText>
      <div className="flex items-center gap-2">
        <p className="text-sm text-muted">These are the accounts clinics see on the portal's Pay Company screen. Deactivate one and it stops collecting.</p>
        {canEdit && (
          <Btn className="ml-auto" onClick={() => setEditing({ ...EMPTY_ACCOUNT })}>
            ＋ Add account
          </Btn>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((a) => (
          <div key={a.id} className={`rounded-lg border bg-surface p-4 shadow-level-1 ${a.isActive ? 'border-line' : 'border-dashed border-line opacity-60'}`}>
            <div className="flex items-center gap-2">
              <b className="text-ink">{a.label}</b>
              {!a.isActive && <span className="rounded bg-surface-2 px-1.5 text-[11px] text-muted">Inactive</span>}
              {canEdit && (
                <button
                  className="ml-auto text-xs text-accent hover:underline"
                  onClick={() =>
                    setEditing({
                      id: a.id,
                      label: a.label,
                      bankName: a.bankName,
                      accountTitle: a.accountTitle,
                      accountNumber: a.accountNumber ?? '',
                      iban: a.iban ?? '',
                      branch: a.branch ?? '',
                      instructions: a.instructions ?? '',
                      sortOrder: a.sortOrder,
                      isActive: a.isActive,
                    })
                  }
                >
                  Edit
                </button>
              )}
            </div>
            <div className="mt-1 text-sm text-ink">{a.bankName}</div>
            <div className="text-sm text-muted">{a.accountTitle}</div>
            {a.accountNumber && <div className="font-mono text-xs text-muted">{a.accountNumber}</div>}
            {a.iban && <div className="font-mono text-xs text-muted">{a.iban}</div>}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-muted">No accounts yet - clinics have nowhere to pay.</p>}
      </div>

      {editing && (
        <Modal
          open
          onClose={() => setEditing(null)}
          title={editing.id ? 'Edit account' : 'Add account'}
          footer={
            <>
              <Btn tone="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Btn>
              <Btn onClick={() => void save()}>Save</Btn>
            </>
          }
        >
          <div className={formGridCls}>
            <Field label="Label *" hint="What the clinic sees, e.g. “Main account”.">
              <input className={inputCls} value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} />
            </Field>
            <Field label="Bank *">
              <input className={inputCls} value={editing.bankName} onChange={(e) => setEditing({ ...editing, bankName: e.target.value })} />
            </Field>
            <Field label="Account title *">
              <input className={inputCls} value={editing.accountTitle} onChange={(e) => setEditing({ ...editing, accountTitle: e.target.value })} />
            </Field>
            <Field label="Account number">
              <input className={inputCls} value={editing.accountNumber} onChange={(e) => setEditing({ ...editing, accountNumber: e.target.value })} />
            </Field>
            <Field label="IBAN">
              <input className={inputCls} value={editing.iban} onChange={(e) => setEditing({ ...editing, iban: e.target.value })} />
            </Field>
            <Field label="Branch">
              <input className={inputCls} value={editing.branch} onChange={(e) => setEditing({ ...editing, branch: e.target.value })} />
            </Field>
            <Field label="Instructions">
              <textarea className={inputCls} rows={2} value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })} />
            </Field>
            <Field label="Order">
              <input type="number" className={inputCls} value={editing.sortOrder} onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) || 0 })} />
            </Field>
            <Field label="Active">
              <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}
